import BenchlingConfig from 'BenchlingConfig';
import PersistentStorage from 'notebook/notes/PersistentStorage';
import {NoteDay} from 'notebook/util/NoteUtilConstants';
import lazyInit from 'util/lazyInit';

// TODO (BNCH-93703): Clean this structure up to not use the EntryStorageStateEnums and instead provide an inline definition for the section state field in the state object.
export interface EntryStorageStateInterface {
  [storageStateType: string]: EntrySectionStateInterface; // specify the types you want
}

export interface EntrySectionStateInterface {
  [puuid: string]: {[property: string]: boolean};
}

export enum EntryStorageStateEnums {
  SECTION_STATE = 'sectionState',
}

export enum EntryStoragePropertyEnums {
  IS_OPEN = 'isOpen',
}

// EntryPersistentStorage calls upon PersistentStorage to manage the session and local storage for entries
class EntryPersistentStorage extends PersistentStorage<EntryStorageStateInterface> {
  DOC_LIKE_TYPE_PREFIXES = ['etr', 'doc', 'tmpl', 'stgentr'];

  insertDay(fileId: string, newDay: NoteDay): void {
    if (!this._isDocumentLikeType(fileId) || !BenchlingConfig.ELN_ENABLE_ENTRY_SECTIONS) {
      return;
    }

    const fileStorage = this.getFileStorage(fileId) ?? {[EntryStorageStateEnums.SECTION_STATE]: {}};

    const fileSectionState = fileStorage[EntryStorageStateEnums.SECTION_STATE] || {};

    fileSectionState[newDay.provenanceUuid] = {[EntryStoragePropertyEnums.IS_OPEN]: true};
    fileStorage[EntryStorageStateEnums.SECTION_STATE] = fileSectionState;

    this.setFileStorage(fileId, fileStorage);
  }

  deleteDay(fileId: string, deleteDay: NoteDay): void {
    if (!this._isDocumentLikeType(fileId) || !BenchlingConfig.ELN_ENABLE_ENTRY_SECTIONS) {
      return;
    }

    const fileStorage: EntryStorageStateInterface | undefined = this.getFileStorage(fileId);

    if (fileStorage && fileStorage[EntryStorageStateEnums.SECTION_STATE]) {
      delete fileStorage[EntryStorageStateEnums.SECTION_STATE][deleteDay.provenanceUuid];
    }

    this.setFileStorage(fileId, fileStorage);
  }

  getFileSectionState(fileId: string, days: Array<object>): EntrySectionStateInterface {
    if (!this._isDocumentLikeType(fileId) || !BenchlingConfig.ELN_ENABLE_ENTRY_SECTIONS) {
      return {};
    }

    const fileStorage = this.getFileStorage(fileId) ?? {[EntryStorageStateEnums.SECTION_STATE]: {}};

    if (Object.keys(fileStorage[EntryStorageStateEnums.SECTION_STATE]).length > 0 || days.length === 0) {
      return fileStorage[EntryStorageStateEnums.SECTION_STATE];
    }
    // all section day PUUIDs are open by default
    const newEntrySectionState: EntrySectionStateInterface = {};

    days.reduce(
      (
        accumulator: EntrySectionStateInterface,
        day: {provenanceUuid: string}
      ): EntrySectionStateInterface => {
        accumulator[day.provenanceUuid] = {[EntryStoragePropertyEnums.IS_OPEN]: true};
        return accumulator;
      },
      newEntrySectionState
    );

    return newEntrySectionState;
  }

  setFileSectionState(fileId: string, fileSectionState: EntrySectionStateInterface): void {
    if (!this._isDocumentLikeType(fileId) || !BenchlingConfig.ELN_ENABLE_ENTRY_SECTIONS) {
      return;
    }

    const fileStorage = this.getFileStorage(fileId) ?? {[EntryStorageStateEnums.SECTION_STATE]: {}};

    fileStorage[EntryStorageStateEnums.SECTION_STATE] = fileSectionState;
    this.setFileStorage(fileId, fileStorage);
  }

  getPropertyValueFromPUUID(
    fileSectionState: EntrySectionStateInterface,
    puuid: string,
    property: string
  ): boolean {
    // Return the value of the fileSectionState's PUUID's property. If the PUUID is absent from the
    // fileSectionState, return true by default.
    return !fileSectionState.hasOwnProperty(puuid) || fileSectionState[puuid][property];
  }

  // only allow document-like files to interact with PersistentStorage
  _isDocumentLikeType(fileId: string): boolean {
    const filePrefix = fileId.split('_')[0];
    return this.DOC_LIKE_TYPE_PREFIXES.includes(filePrefix);
  }
}

const getEntryPersistentStorage = lazyInit<EntryPersistentStorage>(() => new EntryPersistentStorage());

export default getEntryPersistentStorage;
